import { JSX, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import BugReportIcon from "@mui/icons-material/BugReport";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import {
  CadClickViewer3DScriptUrlContext,
  ConfigurationMessages,
  DebugOptionsSelector,
  DebugSpeedDial,
  DebugSpeedDialAction,
} from "@plansoft-configuration-renderer/components";
import {
  Configuration,
  ConfigurationApi,
} from "@plansoft-configuration-renderer/configuration-api";
import {
  ConfigurationController,
  ConfigurationInitializer,
  ConfigurationRenderer,
  HeartbeatController,
} from "@plansoft-configuration-renderer/framework";
import { SettingsSync } from "@plansoft-configuration-renderer/layout";

import ConfigurationHeader from "@/components/ConfigurationHeader";
import { ConfigurationTreeProvider } from "@/context/ConfigurationTreeContext";
import { useEnvContext } from "@/context/EnvContext";
import { useUrlParams } from "@/hooks/useUrlParams";
import { createKeepAliveMiddleware, sessionMiddleware } from "@/tools/helper";

import { ConfigurationStateSpy } from "./ConfigurationStateSpy";
import { CustomRenderer } from "./CustomRenderer";
import "./MinimalRenderer.css";
import CustomCadClickViewer3DController from "./components/CustomCadClickViewer3DController";

export interface MinimalRendererProps {
  /**
   * SessionId for ConfigurationInitializer
   */
  sessionId?: string;
  /**
   * Configuration object id
   */
  cfgObjectId: string;
  /**
   * Configuration language / locale code
   */
  language: string;
  /**
   * The base URL must be mapped by the proxy
   */
  baseUrl: string;
  /**
   * Use a specific layout, only works for the root cfg object
   */
  layoutId?: string;
}

/**
 * This is the base setup for the renderer
 *
 * - the `CsCfgObjectController` starts the configuration
 * - the `ConfigurationController` provides the necessary state management
 */
export function MinimalRenderer(props: MinimalRendererProps): JSX.Element {
  // see setupProxy.js in src folder
  // in production this could be loaded from the database et
  const { baseUrl, sessionId, language } = props;

  const { ENVIRONMENT } = useEnvContext();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { currencyId } = useUrlParams();

  // Custom api instance so every renderer request (incl. keepAlive) carries x-session-id
  const cfgApi = useMemo(
    () =>
      new ConfigurationApi(
        new Configuration({
          basePath: baseUrl,
          middleware: [
            sessionMiddleware,
            createKeepAliveMiddleware(() => setSessionExpired(true)),
          ],
        }),
      ),
    [baseUrl],
  );

  return (
    <CadClickViewer3DScriptUrlContext.Provider value="kim-viewer-3d/Viewer3D.1.9.13.min.js">
      <ConfigurationController
        baseUrl={baseUrl}
        cfgApi={cfgApi}
        key={sessionId}
      >
        {/* ConfigurationStarter starts to configuration but renders nothing. Also see ConfigurationResumer */}
        <ConfigurationInitializer
          sessionId={sessionId}
          localeId={language}
          currencyId={currencyId}
        />
        {/* ConfigurationRenderer prevents the configuration from rendering when there is not cfg object yet */}
        <ConfigurationRenderer>
          {/* Renders nothing, but keeps the session alive. Unmounted once expired to stop further calls. */}
          {!sessionExpired && <HeartbeatController interval={60e3} />}
          {/* Sync settings to redux store, omit if not needed */}
          <SettingsSync />
          {/* Renders configuration messages (dialogs + alerts), e.g. PCD-Info */}
          <ConfigurationMessages />
          {/* Display loading spinner when requests are open */}
          <Box className="layout-container" sx={{ pt: 4 }}>
            {/* Used to manage the state of the tree */}
            <ConfigurationTreeProvider>
              <ConfigurationHeader />
              {/* Renders the configuration */}
              <CustomRenderer />
            </ConfigurationTreeProvider>
          </Box>
          {/* Spy on configuration state changes */}
          {ENVIRONMENT !== "production" && (
            <>
              <Box position="fixed" right="20px" bottom="20px">
                <DebugSpeedDial
                  icon={<BugReportIcon />}
                  defaultIsOpen={false}
                  FabProps={{
                    sx: {
                      backgroundColor: "warning.main",
                    },
                  }}
                >
                  <DebugSpeedDialAction
                    icon={<BugReportIcon />}
                    tooltipTitle={t("show_debug_drawer")}
                    onClick={() => setIsOpen(!isOpen)}
                  />
                </DebugSpeedDial>
              </Box>
              <Drawer
                variant="temporary"
                anchor="right"
                open={isOpen}
                onClose={() => setIsOpen(false)}
              >
                <DebugOptionsSelector />
              </Drawer>
            </>
          )}
          <ConfigurationStateSpy />
          <Dialog open={sessionExpired} disableEscapeKeyDown>
            <DialogTitle>{t("session_expired.title")}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t("session_expired.text")}</DialogContentText>
            </DialogContent>
          </Dialog>
        </ConfigurationRenderer>
        <CustomCadClickViewer3DController />
      </ConfigurationController>
    </CadClickViewer3DScriptUrlContext.Provider>
  );
}
